import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Badge,
  Button,
  Checkbox,
  CheckboxGroup,
  Group,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";
import { TextAreaField } from "@/components/ui/parts";

import { showNotification } from "@/util/userNotifications";

import { reportMapLocationMutation } from "./_graphql";

const COMMENT_MAX_LENGTH = 100;

// サーバーの MapLocationReportReason enum と同じ値・表示順
const REASON_OPTIONS = [
  { value: "photo_mismatch", label: "求人の写真とGoogleマップの建物が違った" },
  { value: "neighboring_building", label: "隣の建物を指していた" },
  { value: "wrong_location", label: "まったく違う場所を指していた" },
  { value: "other", label: "その他" },
] as const;

const OTHER_REASON = "other";

const schema = z
  .object({
    reasons: z
      .array(z.enum(REASON_OPTIONS.map((option) => option.value)))
      .min(1, "理由を1つ以上選択してください"),
    comment: z
      .string()
      .max(
        COMMENT_MAX_LENGTH,
        `コメントは${COMMENT_MAX_LENGTH}文字以内で入力してください`,
      ),
  })
  .superRefine((data, ctx) => {
    // 「その他」は選択肢から内容が分からないため、運営が対応できるようコメントを必須にする
    // （trim は全角スペースも除去するため、空白のみの入力も未入力として扱う）
    if (data.reasons.includes(OTHER_REASON) && data.comment.trim() === "") {
      ctx.addIssue({
        code: "custom",
        path: ["comment"],
        message: "「その他」を選んだ場合はコメントを入力してください",
      });
    }
  });

type Schema = z.infer<typeof schema>;

const defaultValues: Schema = { reasons: [], comment: "" };

type Props = {
  opened: boolean;
  close: () => void;
  temporaryJobOfferId: string;
  // 送信済みで弾かれた場合などに、送信済み表示を最新化するため呼び出し元の情報を再取得する
  onSendError?: () => void;
  // 送信成功時の通知（呼び出し元で送信済み表示へフォーカスを移すため）
  onReported?: () => void;
};

export function MapLocationReportModal(props: Props) {
  const { opened, close, temporaryJobOfferId, onSendError, onReported } = props;

  const {
    control,
    watch,
    handleSubmit,
    reset,
    trigger,
    formState,
    getFieldState,
  } = useForm<Schema>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "onChange",
  });
  const reasons = watch("reasons");
  const comment = watch("comment");
  const otherSelected = reasons.includes(OTHER_REASON);

  // 送信済み表示（mapLocationReported）は mutation の戻り値で Apollo のキャッシュが更新されて切り替わる
  const [mutation, { loading }] = useMutation(reportMapLocationMutation);

  // 送信中に閉じると、失敗したときに入力が消えてモーダルを開いたままにできないため、送信中は閉じさせない
  const handleClose = () => {
    if (loading) return;
    close();
  };

  const onSubmit = async (data: Schema) => {
    await mutation({
      variables: {
        input: {
          temporaryJobOfferId,
          reasons: data.reasons,
          comment: data.comment.trim(),
        },
      },
      onError: (error) => {
        showNotification({
          type: "error",
          message: error.message,
        });
        onSendError?.();
      },
      onCompleted: (res) => {
        if (!res.reportMapLocation) return;
        showNotification({
          type: "info",
          message: "地図・場所の報告を送信しました",
        });
        onReported?.();
        close();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      close={handleClose}
      closeOnEscape={!loading}
      closeOnClickOutside={!loading}
      closeButtonProps={{ disabled: loading }}
      title="地図・場所の報告"
      // レビュー入力モーダルの上に重ねて表示する
      zIndex={1000}
      // キャンセル・送信後は入力内容を破棄する（閉じるアニメーション中に選択が外れて見えないよう、閉じ終わってから）
      onExitTransitionEnd={() => reset(defaultValues)}
    >
      <Stack gap="sm">
        <Text fz="sm" c="dimmed">
          当てはまるものを選んでください（複数選択可）。内容は運営に届き、評価には影響しません。
        </Text>
        {/* CheckboxField は再レンダーのたびに子要素を作り直してフォーカスが外れるため、Controller で直接組み立てる */}
        <Controller
          control={control}
          name="reasons"
          render={({ field, fieldState }) => (
            <CheckboxGroup
              label="理由"
              value={field.value}
              onChange={(value: string[]) => {
                field.onChange(value);
                // コメントの必須条件は理由の選択で変わるため、コメントも再検証してエラー表示を追従させる
                // （入力して消すと未変更扱いに戻るため、エラー表示中かどうかも見る）
                const { isDirty, invalid } = getFieldState("comment");
                if (isDirty || invalid) trigger("comment");
              }}
              error={fieldState.error?.message}
            >
              <Stack gap="xs" mt={4}>
                {REASON_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </Stack>
            </CheckboxGroup>
          )}
        />
        <Stack gap={4}>
          <Group gap={6} align="center">
            <Text fz="sm" fw={500}>
              コメント
            </Text>
            <Badge
              size="sm"
              color={otherSelected ? "red" : "gray"}
              variant={otherSelected ? "filled" : "light"}
              style={{ textTransform: "none" }}
            >
              {otherSelected ? "必須" : "任意"}
            </Badge>
          </Group>
          <TextAreaField
            name="comment"
            control={control}
            // レビュー入力の comment と id が重複しないよう明示する
            id="map-location-report-comment"
            aria-label="コメント"
            aria-required={otherSelected}
            rows={2}
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={
              otherSelected
                ? "その他の内容を入力してください"
                : "補足があれば入力してください（任意）"
            }
          />
          <Text fz="xs" c="dimmed" ta="right">
            {comment.length}/{COMMENT_MAX_LENGTH}
          </Text>
        </Stack>
        <Group justify="flex-end" gap="xs">
          <Button
            variant="white"
            size="sm"
            onClick={handleClose}
            disabled={loading}
          >
            キャンセル
          </Button>
          {/* 将来このモーダルに form を置いても、React の Portal 越しの submit 伝播でレビュー送信を誘発しないよう onClick で送信する */}
          <Button
            type="button"
            size="sm"
            color="teal"
            loading={loading}
            // 送信成功後、閉じるアニメーションが終わるまでの間に再送信されないよう、閉じ始めたら押せなくする
            disabled={!opened || !formState.isValid || formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            送信
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
